import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  addMissingTooltipFields,
  flattenObjectHierarchy,
  generatePropertiesToNodesTree,
  groupHelpContentProperties,
} from 'pages/help-content-management/utils';

import * as helpContentManagementService from 'services/help-content-management';

import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import { useCheckPermissions } from 'hooks';

const PERMISSION_MANAGE_HELP_CONTENT = [
  {
    action: ABILITY_ACTION.MANAGE,
    subject: ABILITY_SUBJECT.HELP_CONTENT,
  },
];

const useCheckHelpContentManagement = () => {
  return useCheckPermissions(PERMISSION_MANAGE_HELP_CONTENT);
};

export const useGetHelpContentFields = () => {
  const hasHelpContentManagement = useCheckHelpContentManagement();

  const queries = useQuery({
    queryKey: ['help-content-management', 'fields'],
    queryFn: async () => {
      const response =
        await helpContentManagementService.getHelpContentAllFields();

      if (response?.isSuccess) {
        return response.data;
      }
    },
    staleTime: Infinity,
    retry: 0,
    enabled: Boolean(hasHelpContentManagement),
  });

  const enhancedFields = useMemo(
    () =>
      addMissingTooltipFields(queries?.data?.advanceSearch?.[0]?.fields ?? []),
    [queries?.data]
  );

  return {
    enhancedFields,
    helpContentFields: useMemo(() => enhancedFields, [enhancedFields]),
    treeData: useMemo(() => {
      return generatePropertiesToNodesTree(enhancedFields);
    }, [enhancedFields]),
    groupFields: useMemo(() => {
      return groupHelpContentProperties(enhancedFields);
    }, [enhancedFields]),
    flatHelpContentFields: useMemo(() => {
      return flattenObjectHierarchy(enhancedFields);
    }, [enhancedFields]),
    ...queries,
  };
};
